import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CheckCircle,
  ClipboardCheck,
  Package,
  AlertTriangle,
  Upload,
  Trash2,
  Eye,
  Calendar,
} from "lucide-react";
import {
  APL_LINK,
  get_grn_by_purchase_id,
  grn_dashboard_list,
  save_grn_verify,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";
import { formatDateStringdot } from "../CommonJquery/CommonJquery.js";
import Loader from "../components/layout/Loader.js";
import { useToast } from "../components/ui/use-toast";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

/** Chhoti value pe zyada decimals — 60 g in KG = 0.06, "0.00" nahi */
const fmtQty = (v) => {
  const n = num(v);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  let d = 2;
  if (abs < 0.001) d = 6;
  else if (abs < 0.01) d = 5;
  else if (abs < 1) d = 4;
  else if (abs < 100) d = 3;
  return String(parseFloat(n.toFixed(d)));
};

const money = (v) => `₹${num(v).toFixed(2)}`;

export default function GRNVerification() {
  const { toast } = useToast();

  const [selectedPO, setSelectedPO] = useState(null);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [poList, setPOList] = useState([]);
  const [challan_no, setChallanNumber] = useState("");
  const [challanImagesPreview, setChallanImagesPreview] = useState([]);
  const [challanImageFiles, setChallanImageFiles] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [gst, setGst] = useState("0");
  const [labourCharge, setLabourCharge] = useState("0");
  const [transportCharge, setTransportCharge] = useState("0");
  const [issueToStore, setIssueToStore] = useState("");
  const [poItems, setPoItems] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [fallbackPurchaseUnitTypes, setfallbackPurchaseUnitTypes] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);

  const [formErrors, setFormErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  const fileInputRef = useRef(null);
  const challanInputRef = useRef(null);

  const handleError = (msg) =>
    toast({ title: "Error", description: msg, variant: "destructive" });
  const handleSuccess = (msg) => toast({ title: "Success", description: msg });

  /* ---------------- unit helpers ---------------- */

  // Units ek map me — id se lookup fast ho
  const unitMap = useMemo(() => {
    const m = {};
    (unitTypes || []).forEach((u) => {
      m[u.primary_id] = u;
    });
    return m;
  }, [unitTypes]);

  // Base unit ka conversion_to_base_unit = 1 hota hai (0 nahi).
  const convFactor = useCallback(
    (unitId) => {
      const f = num(unitMap[unitId]?.conversion_to_base_unit, 1);
      return f > 0 ? f : 1;
    },
    [unitMap],
  );

  const unitLabel = useCallback(
    (unitId) =>
      unitMap[unitId]?.short_code_name || unitMap[unitId]?.unit_name || "",
    [unitMap],
  );

  const toBase = useCallback(
    (qty, unitId) => num(qty) * convFactor(unitId),
    [convFactor],
  );

  // Sirf same family ke units (Atta ke liye ML nahi aana chahiye)
  const unitsForItem = useCallback(
    (item) => {
      const anchor = unitMap[item.item_unit] || unitMap[item.default_unit_id];
      if (!anchor) return unitTypes;
      const parentId = Number(anchor.parent_unit_id) || Number(anchor.primary_id);
      return (unitTypes || []).filter(
        (u) => Number(u.parent_unit_id) === parentId,
      );
    },
    [unitMap, unitTypes],
  );

  /* ---------------- errors ---------------- */

  const clearFormError = (field) =>
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const clearItemError = (id, field) =>
    setItemErrors((prev) => {
      if (!prev[id]?.[field]) return prev;
      const next = { ...prev };
      next[id] = { ...next[id] };
      delete next[id][field];
      if (!Object.keys(next[id]).length) delete next[id];
      return next;
    });

  /* ---------------- data ---------------- */

  const fetchDashboard = async () => {
    setShowLoader(true);
    const fd = new FormData();
    fd.append("status", "all");

    await server_post_data(grn_dashboard_list, fd)
      .then((res) => {
        if (res.data.error) {
          handleError(res.data.message);
        } else {
          setPOList(res.data.message.list || []);
          setDashboard(res.data.message.dashboard || {});
          setUnitTypes(res.data.message.units || []);
          setfallbackPurchaseUnitTypes(res.data.message.purchase_type || []);
        }
      })
      .catch(() => handleError("Failed to load dashboard"))
      .finally(() => setShowLoader(false));
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartVerification = async (po, mode = "verify") => {
    setShowLoader(true);
    const fd = new FormData();
    fd.append("purchase_id", po.purchase_id);

    await server_post_data(get_grn_by_purchase_id, fd)
      .then((res) => {
        if (res.data.error) {
          handleError(res.data.message);
          return;
        }

        const data = res.data.message.data_list?.[0];
        if (!data) {
          handleError("No PO data found");
          return;
        }

        setSelectedPO(data);
        setChallanNumber(data.challan_no || "");
        setRemarks(data.remarks || "");
        setGst(String(data.gst_per ?? "0"));
        setLabourCharge(String(data.extra_amount ?? "0"));
        setTransportCharge(String(data.transport_amount ?? "0"));

        const depts = res.data.message.departments || [];
        setDepartmentsList(depts);

        // Default = Main Store (agar hai)
        const mainStore = depts.find((d) => Number(d.is_main_store) === 1);
        setIssueToStore(
          data.direct_to
            ? String(data.direct_to)
            : mainStore
              ? String(mainStore.primary_id)
              : "",
        );

        setFormErrors({});
        setItemErrors({});

        const enhanced = (res.data.message.items || []).map((item) => {
          const isLoose = (item.purchase_type || "").toLowerCase() === "loose";

          // PO ka unit — yahi default receiving unit hoga
          const poUnitId =
            Number(item.item_unit_entry) || Number(item.item_unit) || 0;

          const kgPerPack = isLoose ? 1 : num(item.each_container, 1) || 1;
          const orderedTotal = num(item.item_qty_entry);
          const orderedPacks = isLoose ? orderedTotal : orderedTotal / kgPerPack;

          let purchaseType = item.purchase_type || "Loose";
          if (purchaseType.toLowerCase() === "packets") purchaseType = "Packets";

          return {
            ...item,
            // receiving fields — khaali se shuru, user bharega
            receivedPacks: "",
            kgPerPack,
            totalReceived: 0,
            item_unit: poUnitId,
            item_unit_type: item.item_unit_entry_name || item.item_unit_type || "",
            item_unit_price: "",
            total_amount: "",
            purchaseUnitType: purchaseType,
            originalPurchaseUnitType: item.purchase_type,
            master_purchase_type: item.master_purchase_type,
            master_each_container: item.master_each_container,
            default_unit_id: item.default_unit_id,
            orderedPacks,
            orderedTotal,
          };
        });

        setPoItems(enhanced);

        const existingImages = data.challan_image
          ? data.challan_image
              .split("@@@")
              .filter(Boolean)
              .map((p) => APL_LINK + p.trim())
          : [];

        if (mode === "verify") {
          setChallanImagesPreview([]);
          setChallanImageFiles([]);
          setShowVerifyDialog(true);
        } else {
          setChallanImagesPreview(existingImages);
          setShowViewDialog(true);
        }
      })
      .catch(() => handleError("Network error"))
      .finally(() => setShowLoader(false));
  };

  /* ---------------- images ---------------- */

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const ok = files.filter((f) => {
      if (f.size > 5 * 1024 * 1024) {
        handleError(`"${f.name}" is larger than 5MB`);
        return false;
      }
      return true;
    });
    if (!ok.length) {
      e.target.value = "";
      return;
    }

    Promise.all(
      ok.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          }),
      ),
    ).then((previews) => {
      setChallanImagesPreview((prev) => [...prev, ...previews]);
      setChallanImageFiles((prev) => {
        const updated = [...prev, ...ok];
        if (updated.length) clearFormError("challanImages");
        return updated;
      });
    });

    e.target.value = "";
  };

  const removeImage = (index) => {
    setChallanImagesPreview((prev) => prev.filter((_, i) => i !== index));
    setChallanImageFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (!updated.length) {
        setFormErrors((fe) => ({
          ...fe,
          challanImages: "Upload at least one challan image",
        }));
      }
      return updated;
    });
  };

  /* ---------------- row maths ---------------- */

  /**
   * Ek row ka poora hisaab.
   *   totalReceived = packs x per-pack (loose me seedha packs)
   *   base qty      = totalReceived x conversion_to_base_unit
   *   rate          = total_amount / totalReceived   (per selected unit)
   */
  const rowCalc = useCallback(
    (item) => {
      const isLoose = item.purchaseUnitType?.toLowerCase() === "loose";
      const packs = num(item.receivedPacks);
      const perPack = num(item.kgPerPack, 1) || 1;

      const totalReceived = isLoose ? packs : packs * perPack;
      const baseQty = toBase(totalReceived, item.item_unit);

      const totalAmount = num(item.total_amount);
      const ratePerUnit =
        totalReceived > 0 ? totalAmount / totalReceived : num(item.item_unit_price);
      const ratePerBase = baseQty > 0 ? totalAmount / baseQty : 0;

      const orderedPacks = num(item.orderedPacks);
      const hasDiscrepancy = packs > 0 && Math.abs(packs - orderedPacks) > 0.0001;

      return {
        isLoose,
        packs,
        perPack,
        totalReceived,
        baseQty,
        totalAmount,
        ratePerUnit,
        ratePerBase,
        hasDiscrepancy,
      };
    },
    [toBase],
  );

  const handleItemValueChange = (primary_id, field, value) => {
    setPoItems((prev) =>
      prev.map((item) => {
        if (item.primary_id !== primary_id) return item;

        const updated = { ...item };

        // ─────────────────────────────────────────────────────────────
        // UNIT badla -> quantity ko nayi unit me CONVERT karo.
        // Purana code sirf id badal deta tha, number wahi rehta tha —
        // isliye 10 KG chun ke G kiya to backend ko 10 gram jaata tha.
        // ─────────────────────────────────────────────────────────────
        if (field === "item_unit") {
          const newUnitId = Number(value);
          const oldUnitId = Number(item.item_unit);

          const oldF = convFactor(oldUnitId);
          const newF = convFactor(newUnitId);

          if (oldUnitId && newUnitId && oldF !== newF) {
            const packs = num(item.receivedPacks);
            const perPack = num(item.kgPerPack, 1) || 1;

            if (packs > 0) {
              updated.receivedPacks = String(
                parseFloat(((packs * oldF) / newF).toFixed(6)),
              );
            }
            if (item.purchaseUnitType?.toLowerCase() !== "loose" && perPack > 0) {
              updated.kgPerPack = parseFloat(((perPack * oldF) / newF).toFixed(6));
            }
          }

          updated.item_unit = newUnitId;
          updated.item_unit_type = unitLabel(newUnitId);
        } else if (field === "receivedPacks") {
          if (value !== "" && !/^\d*\.?\d{0,4}$/.test(value)) return item;
          updated.receivedPacks = value;
        } else if (field === "kgPerPack") {
          if (value !== "" && !/^\d*\.?\d{0,4}$/.test(value)) return item;
          updated.kgPerPack = value;
        } else if (field === "total_amount") {
          if (value !== "" && !/^\d*\.?\d{0,4}$/.test(value)) return item;
          updated.total_amount = value;
        } else if (field === "item_unit_price") {
          if (value !== "" && !/^\d*\.?\d{0,6}$/.test(value)) return item;
          updated.item_unit_price = value;

          // rate se total nikalo
          const isLoose = updated.purchaseUnitType?.toLowerCase() === "loose";
          const packs = num(updated.receivedPacks);
          const perPack = num(updated.kgPerPack, 1) || 1;
          const totalQty = isLoose ? packs : packs * perPack;
          if (totalQty > 0 && num(value) > 0) {
            updated.total_amount = String(
              parseFloat((totalQty * num(value)).toFixed(4)),
            );
          }
        }  else if (field === "purchaseUnitType") {
          updated.purchaseUnitType = value;
          if (value.toLowerCase() === "loose") {
            updated.kgPerPack = 1;
                    } else if (num(updated.kgPerPack) <= 1) {
            updated.kgPerPack =
              num(item.master_each_container, 1) || num(item.each_container, 1) || 1;
          }
        }

        return updated;
      }),
    );

    clearItemError(primary_id, field);
    if (field === "receivedPacks" && (value === "" || num(value) === 0)) {
      clearItemError(primary_id, "total_amount");
    }
  };

  /* ---------------- totals ---------------- */

  const itemsTotal = useMemo(
    () => poItems.reduce((sum, it) => sum + num(it.total_amount), 0),
    [poItems],
  );

  const gstAmount = useMemo(
    () => itemsTotal * (num(gst) / 100),
    [itemsTotal, gst],
  );

  const grandTotal = useMemo(
    () => itemsTotal + gstAmount + num(labourCharge) + num(transportCharge),
    [itemsTotal, gstAmount, labourCharge, transportCharge],
  );

  // Landed cost = base price + uska overhead hissa
  const overheadFactor = useMemo(() => {
    const extras = gstAmount + num(labourCharge) + num(transportCharge);
    return itemsTotal > 0 ? extras / itemsTotal : 0;
  }, [gstAmount, labourCharge, transportCharge, itemsTotal]);

  /* ---------------- validation ---------------- */

  const validateForm = () => {
    const fErr = {};
    const iErr = {};

    if (!challan_no.trim()) fErr.challan_no = "Challan/Bill number is required";
    if (!challanImageFiles.length)
      fErr.challanImages = "Upload at least one challan image";
    if (!issueToStore) fErr.issueToStore = "Select where the goods go";

    let anyReceived = false;

    poItems.forEach((item) => {
      const c = rowCalc(item);
      const e = {};

      if (item.receivedPacks === "" || item.receivedPacks === null) {
        e.receivedPacks = "Required";
      }

      if (!c.isLoose && num(item.kgPerPack) <= 0) {
        e.kgPerPack = "Required";
      }

      if (!item.item_unit) e.item_unit = "Required";

      if (c.packs > 0) {
        anyReceived = true;
        if (c.totalAmount <= 0) e.total_amount = "Amount required";
      }

      if (Object.keys(e).length) iErr[item.primary_id] = e;
    });

    if (!anyReceived && !Object.keys(iErr).length) {
      fErr.noItems = "Enter received quantity for at least one item";
    }

    setFormErrors(fErr);
    setItemErrors(iErr);

    const valid = !Object.keys(fErr).length && !Object.keys(iErr).length;

    if (!valid) {
      if (fErr.challan_no && challanInputRef.current) {
        challanInputRef.current.focus();
      }
      handleError("Please fix the highlighted fields");
    }

    return valid;
  };

  /* ---------------- submit ---------------- */

  const handleCompleteVerification = async () => {
    if (!validateForm()) return;

    setShowLoader(true);

    const payloadItems = poItems.map((item) => {
      const c = rowCalc(item);
      return {
        item_id: item.item_id,
        item_name: item.item_name,

        // quantity — selected unit me
        receivedPacks: c.packs,
        kgPerPack: c.perPack,
        totalReceived: parseFloat(c.totalReceived.toFixed(4)),

        // unit — id aur naam dono ek hi unit ke
        item_unit: Number(item.item_unit),
        item_unit_type: unitLabel(item.item_unit),
        purchaseUnitType: item.purchaseUnitType,

        // price — per selected unit
        item_unit_price: parseFloat(c.ratePerUnit.toFixed(6)),
        total_amount: parseFloat(c.totalAmount.toFixed(4)),
      };
    });

    console.log("save_grn_verify payload:", {
      department_id: issueToStore,
      items: payloadItems,
    });

    const fd = new FormData();
    fd.append("purchase_id", selectedPO.purchase_id);
    fd.append("gst_per", gst);
    fd.append("gst_amount", gstAmount.toFixed(2));
    fd.append("grnd_total", grandTotal.toFixed(2));
    fd.append("transport_amount", transportCharge);
    fd.append("extra_amount", labourCharge);
    fd.append("challan_no", challan_no);
    fd.append("remarks", remarks);
    fd.append("department_id", String(issueToStore));
    fd.append("items", JSON.stringify(payloadItems));

    challanImageFiles.forEach((file) =>
      fd.append("challan_image_new_loop", file),
    );

    await server_post_data(save_grn_verify, fd)
      .then((res) => {
        if (res.data.error) {
          handleError(res.data.message);
        } else {
          handleSuccess("Verification completed");
          setShowVerifyDialog(false);
          setSelectedPO(null);
          setChallanNumber("");
          setChallanImagesPreview([]);
          setChallanImageFiles([]);
          setRemarks("");
          setGst("0");
          setLabourCharge("0");
          setTransportCharge("0");
          setIssueToStore("");
          setPoItems([]);
          setFormErrors({});
          setItemErrors({});
          fetchDashboard();
        }
      })
      .catch(() => handleError("Save failed"))
      .finally(() => setShowLoader(false));
  };

  /* ---------------- ui bits ---------------- */

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "pending")
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-700 border-amber-300"
        >
          <Package className="w-3 h-3 mr-1" /> Awaiting
        </Badge>
      );
    if (s === "completed")
      return (
        <Badge
          variant="outline"
          className="bg-emerald-50 text-emerald-700 border-emerald-300"
        >
          <CheckCircle className="w-3 h-3 mr-1" /> Verified
        </Badge>
      );
    if (s === "discrepancy")
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-300"
        >
          <AlertTriangle className="w-3 h-3 mr-1" /> Discrepancy
        </Badge>
      );
    return <Badge variant="outline">{status || "Unknown"}</Badge>;
  };

  const ErrMsg = ({ msg }) =>
    msg ? <p className="text-xs text-red-500 mt-1">{msg}</p> : null;

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header
        title="Receiving Verification"
        subtitle="Verify received goods against Purchase Order and Challan"
      />

      <div className="p-6 space-y-6">
        {/* Workflow */}
        <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
          <span className="px-3 py-1 rounded bg-muted text-muted-foreground">
            1. Create PO
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-3 py-1 rounded bg-muted text-muted-foreground">
            2. Admin Approval
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-3 py-1 rounded bg-muted text-muted-foreground">
            3. Vendor Order
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-3 py-1 rounded bg-primary/20 text-primary font-medium">
            4. Challan/Bill Verify
          </span>
        </div>

        {/* Dashboard */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Awaiting Verification
                  </p>
                  <p className="text-2xl font-bold">
                    {dashboard.pending_grn || 0}
                  </p>
                </div>
                <Package className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Verified</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {dashboard.verified_grn || 0}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard.grn_this_month || 0}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PO list */}
        <Card>
          <CardHeader>
            <CardTitle>Delivered Orders - Receiving Verification</CardTitle>
            <CardDescription>
              Verify received items, upload challan, complete receiving
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Entry Date</TableHead>
                    <TableHead>Challan/Bill</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poList.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-10 text-muted-foreground"
                      >
                        No purchase orders waiting for verification
                      </TableCell>
                    </TableRow>
                  ) : (
                    poList.map((po) => (
                      <TableRow key={po.purchase_id}>
                        <TableCell className="font-medium">
                          {po.purchase_no}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{po.department_name}</Badge>
                        </TableCell>
                        <TableCell>{po.vendor_name}</TableCell>
                        <TableCell>
                          {formatDateStringdot(po.createdAt)}
                        </TableCell>
                        <TableCell>
                          {po.challan_no || (
                            <span className="text-muted-foreground text-sm">
                              Not uploaded
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {money(po.grand_amount)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(po.approve_status)}
                        </TableCell>
                        <TableCell className="text-center space-x-2">
                          {po.approve_status === "pending" ? (
                            <Button
                              size="sm"
                              onClick={() =>
                                handleStartVerification(po, "verify")
                              }
                            >
                              <ClipboardCheck className="w-4 h-4 mr-1" />
                              Verify
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleStartVerification(po, "view")
                              }
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* ── VERIFY DIALOG ─────────────────────────────────────────── */}
        <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
          <DialogContent className="max-w-[95vw] max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Receiving Verification - {selectedPO?.purchase_no}
              </DialogTitle>
              <DialogDescription>
                Upload challan images and verify received quantities &amp;
                amounts
              </DialogDescription>
            </DialogHeader>

            {selectedPO && (
              <div className="space-y-6 pt-2">
                {/* Challan details */}
                <Card className="bg-muted/40">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Challan/Bill Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="space-y-2">
                        <Label>Challan/Bill No. *</Label>
                        <Input
                          ref={challanInputRef}
                          value={challan_no}
                          onChange={(e) => {
                            setChallanNumber(e.target.value);
                            if (e.target.value.trim())
                              clearFormError("challan_no");
                          }}
                          placeholder="Enter challan number from Vendor"
                          className={
                            formErrors.challan_no
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }
                        />
                        <ErrMsg msg={formErrors.challan_no} />
                      </div>

                      <div className="space-y-2">
                        <Label>Vendor</Label>
                        <Input
                          value={selectedPO.vendor_name}
                          disabled
                          className="bg-muted"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Goods Received At *</Label>
                        <Select
                          value={issueToStore ? String(issueToStore) : ""}
                          onValueChange={(v) => {
                            setIssueToStore(v);
                            clearFormError("issueToStore");
                          }}
                        >
                          <SelectTrigger
                            className={
                              formErrors.issueToStore ? "border-red-500" : ""
                            }
                          >
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            {departmentsList.map((dept) => (
                              <SelectItem
                                key={dept.primary_id}
                                value={String(dept.primary_id)}
                              >
                                {dept.department_name}
                                {Number(dept.is_main_store) === 1
                                  ? " (Main Store)"
                                  : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <ErrMsg msg={formErrors.issueToStore} />
                      </div>
                    </div>

                    {/* Images */}
                    <div className="space-y-2">
                      <Label>Challan/Bill Images * (Multiple allowed)</Label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 ${
                          formErrors.challanImages ? "border-red-500" : ""
                        }`}
                      >
                        {challanImagesPreview.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {challanImagesPreview.map((src, i) => (
                              <div key={i} className="relative group">
                                <img
                                  src={src}
                                  alt={`Challan ${i + 1}`}
                                  className="w-full h-32 object-cover rounded shadow"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute -top-2 -right-2 h-7 w-7 opacity-0 group-hover:opacity-100"
                                  onClick={() => removeImage(i)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <div
                              className="border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary h-32"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                              <span className="text-sm text-muted-foreground">
                                Add more
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <Button
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Images
                            </Button>
                            <p className="text-sm text-muted-foreground mt-3">
                              Max 5MB per image • Multiple files allowed
                            </p>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                      <ErrMsg msg={formErrors.challanImages} />
                    </div>
                  </CardContent>
                </Card>

                {/* Items */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">
                    Verify Received Items
                  </h3>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    • <strong>Loose</strong>: enter the quantity directly
                    <br />• <strong>Packets / Jar / Bottle</strong>: enter how
                    many packs came, and how much is in each pack
                  </div>
                  <ErrMsg msg={formErrors.noItems} />

                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-center">Ordered</TableHead>
                          <TableHead>Purchase Type</TableHead>
                          <TableHead className="text-center">
                            Per Pack
                          </TableHead>
                          <TableHead className="text-center">
                            Packs Received
                          </TableHead>
                          <TableHead className="text-center">Unit</TableHead>
                          <TableHead className="text-center">
                            Total Received
                          </TableHead>
                          <TableHead className="text-center">
                            Total Amount
                          </TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">
                            Prev Rate
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poItems.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              className="text-center py-12 text-muted-foreground"
                            >
                              No items found in this order
                            </TableCell>
                          </TableRow>
                        ) : (
                          poItems.map((item) => {
                            const c = rowCalc(item);
                            const iErr = itemErrors[item.primary_id] || {};
                            const uLabel = unitLabel(item.item_unit);
                            const baseName = unitLabel(
                              unitMap[item.item_unit]?.parent_unit_id,
                            );
                            const opts = unitsForItem(item);

                            return (
                              <TableRow
                                key={item.primary_id}
                                className={
                                  c.hasDiscrepancy ? "bg-amber-50/60" : ""
                                }
                              >
                                {/* Item */}
                                <TableCell className="font-medium">
                                  <div className="flex flex-col">
                                    <span>
                                      {item.item_alter_name ||
                                        item.item_name ||
                                        "—"}
                                    </span>
                                    {item.item_name &&
                                      item.item_alter_name &&
                                      item.item_name.trim().toLowerCase() !==
                                        item.item_alter_name
                                          .trim()
                                          .toLowerCase() && (
                                        <span className="text-[10px] text-muted-foreground leading-none mt-1">
                                          ({item.item_name})
                                        </span>
                                      )}
                                  </div>
                                </TableCell>

                                {/* Ordered */}
                                <TableCell className="text-center">
                                  <span className="font-semibold">
                                    {fmtQty(item.orderedTotal)}
                                  </span>
                                  <p className="text-[11px] text-muted-foreground">
                                    {item.item_unit_entry_name ||
                                      item.item_unit_type}
                                  </p>
                                  {item.purchase_type?.toLowerCase() !==
                                    "loose" && (
                                    <p className="text-[11px] text-muted-foreground">
                                      {item.each_container} × {item.purchase_qty}
                                    </p>
                                  )}
                                </TableCell>

                                {/* Purchase type */}
                                <TableCell>
                                  <Select
                                    value={item.purchaseUnitType || "Loose"}
                                    onValueChange={(v) =>
                                      handleItemValueChange(
                                        item.primary_id,
                                        "purchaseUnitType",
                                        v,
                                      )
                                    }
                                  >
                                    <SelectTrigger className="w-28 mx-auto">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {fallbackPurchaseUnitTypes
  .filter(
    (t) =>
      t.purchase_type_name === "Loose" ||
      t.purchase_type_name === item.originalPurchaseUnitType ||
      t.purchase_type_name === item.master_purchase_type,
  )
  .map((t) => (
                                          <SelectItem
                                            key={t.primary_id}
                                            value={t.purchase_type_name}
                                          >
                                            {t.purchase_type_name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>

                                {/* Per pack */}
                                <TableCell className="text-center">
                                  {c.isLoose ? (
                                    <span className="text-muted-foreground text-sm">
                                      —
                                    </span>
                                  ) : (
                                    <>
                                      <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={item.kgPerPack ?? ""}
                                        onChange={(e) =>
                                          handleItemValueChange(
                                            item.primary_id,
                                            "kgPerPack",
                                            e.target.value,
                                          )
                                        }
                                        className={`w-20 text-center mx-auto ${
                                          iErr.kgPerPack ? "border-red-500" : ""
                                        }`}
                                      />
                                      <p className="text-[11px] text-muted-foreground mt-1">
                                        {uLabel} per {item.purchaseUnitType}
                                      </p>
                                      <ErrMsg msg={iErr.kgPerPack} />
                                    </>
                                  )}
                                </TableCell>

                                {/* Packs received */}
                                <TableCell className="text-center">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={item.receivedPacks ?? ""}
                                    onChange={(e) =>
                                      handleItemValueChange(
                                        item.primary_id,
                                        "receivedPacks",
                                        e.target.value,
                                      )
                                    }
                                    className={`w-24 text-center mx-auto ${
                                      iErr.receivedPacks ? "border-red-500" : ""
                                    }`}
                                  />
                                  <p className="text-[11px] text-muted-foreground mt-1">
                                    {c.isLoose ? uLabel : item.purchaseUnitType}
                                  </p>
                                  <ErrMsg msg={iErr.receivedPacks} />
                                  {c.hasDiscrepancy && (
                                    <p className="text-[11px] text-amber-600 mt-1">
                                      Ordered {fmtQty(item.orderedPacks)}
                                    </p>
                                  )}
                                </TableCell>

                                {/* Unit */}
                                <TableCell className="text-center">
                                  <Select
                                    value={
                                      item.item_unit
                                        ? String(item.item_unit)
                                        : ""
                                    }
                                    onValueChange={(v) =>
                                      handleItemValueChange(
                                        item.primary_id,
                                        "item_unit",
                                        v,
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className={`w-[90px] mx-auto ${
                                        iErr.item_unit ? "border-red-500" : ""
                                      }`}
                                    >
                                      <SelectValue placeholder="Unit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {opts.map((u) => (
                                        <SelectItem
                                          key={u.primary_id}
                                          value={String(u.primary_id)}
                                        >
                                          {u.short_code_name || u.unit_name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <ErrMsg msg={iErr.item_unit} />
                                </TableCell>

                                {/* Total received */}
                                <TableCell className="text-center font-medium">
                                  {fmtQty(c.totalReceived)} {uLabel}
                                  {c.baseQty > 0 &&
                                    baseName &&
                                    baseName !== uLabel && (
                                      <p className="text-[11px] text-muted-foreground">
                                        = {fmtQty(c.baseQty)} {baseName}
                                      </p>
                                    )}
                                </TableCell>

                                {/* Total amount */}
                                <TableCell className="text-center">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={item.total_amount ?? ""}
                                    onChange={(e) =>
                                      handleItemValueChange(
                                        item.primary_id,
                                        "total_amount",
                                        e.target.value,
                                      )
                                    }
                                    className={`w-32 text-right ml-auto ${
                                      iErr.total_amount ? "border-red-500" : ""
                                    }`}
                                  />
                                  <ErrMsg msg={iErr.total_amount} />
                                </TableCell>

                                {/* Rate */}
                                <TableCell className="text-right font-medium">
                                  {c.totalReceived > 0 ? (
                                    <>
                                      {money(c.ratePerUnit)}
                                      <p className="text-[11px] text-muted-foreground">
                                        per {uLabel}
                                      </p>
                                      {overheadFactor > 0 && (
                                        <p className="text-[11px] text-muted-foreground">
                                          landed{" "}
                                          {money(
                                            c.ratePerUnit * (1 + overheadFactor),
                                          )}
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-muted-foreground">
                                      —
                                    </span>
                                  )}
                                </TableCell>

                                {/* Prev rate */}
                                <TableCell className="text-right text-muted-foreground">
                                  {num(item.last_rate) > 0
                                    ? money(item.last_rate)
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Charges */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>GST (%)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={gst}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setGst(v);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GST Amount</Label>
                    <Input
                      value={gstAmount.toFixed(2)}
                      readOnly
                      tabIndex={-1}
                      className="bg-muted"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Labour Charge</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={labourCharge}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v))
                          setLabourCharge(v);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transport Charge</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={transportCharge}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v))
                          setTransportCharge(v);
                      }}
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-full md:w-80 space-y-1 p-4 bg-muted/30 rounded-lg border text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Items subtotal
                      </span>
                      <span>{money(itemsTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        GST ({num(gst)}%)
                      </span>
                      <span>{money(gstAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Labour</span>
                      <span>{money(labourCharge)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transport</span>
                      <span>{money(transportCharge)}</span>
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t font-semibold text-base">
                      <span>Grand Total</span>
                      <span className="text-primary">{money(grandTotal)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Remarks (Optional)</Label>
                  <Textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add notes about delivery, damages, discrepancies..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowVerifyDialog(false)}
                disabled={showLoader}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCompleteVerification}
                disabled={showLoader}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Verification
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── VIEW DIALOG ───────────────────────────────────────────── */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Receiving Details - {selectedPO?.purchase_no}
              </DialogTitle>
            </DialogHeader>

            {selectedPO && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Vendor</p>
                    <p className="font-medium">{selectedPO.vendor_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PO Date</p>
                    <p className="font-medium">
                      {formatDateStringdot(selectedPO.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Challan No.</p>
                    <p className="font-medium">
                      {selectedPO.challan_no || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Received At
                    </p>
                    <p className="font-medium">
                      {selectedPO.department_name || "—"}
                    </p>
                  </div>
                </div>

                {challanImagesPreview.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Challan Images</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {challanImagesPreview.map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt={`Challan ${i + 1}`}
                          className="rounded border object-cover h-48 w-full"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="font-medium mb-2">Received Items</p>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-center">Ordered</TableHead>
                          <TableHead className="text-center">
                            Received
                          </TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">
                            Landed Rate
                          </TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poItems.map((item) => {
                          const isLoose =
                            (item.purchase_type || "").toLowerCase() ===
                            "loose";
                          return (
                            <TableRow key={item.primary_id}>
                              <TableCell className="font-medium">
                                {item.item_name}
                              </TableCell>

                              <TableCell className="text-center">
                                {fmtQty(item.item_qty_entry)}{" "}
                                <span className="text-[11px] text-muted-foreground">
                                  {item.item_unit_entry_name ||
                                    item.item_unit_type}
                                </span>
                                {!isLoose && (
                                  <p className="text-[11px] text-muted-foreground">
                                    {item.each_container} × {item.purchase_qty}
                                  </p>
                                )}
                              </TableCell>

                              <TableCell className="text-center">
                                {fmtQty(item.received_item_qty)}{" "}
                                <span className="text-[11px] text-muted-foreground">
                                  {item.received_unit_name}
                                </span>
                                {!isLoose &&
                                  num(item.received_pack) > 0 && (
                                    <p className="text-[11px] text-muted-foreground">
                                      {item.received_pack} ×{" "}
                                      {fmtQty(item.received_item_qty_entry)}
                                    </p>
                                  )}
                              </TableCell>

                              <TableCell className="text-right">
                                {num(item.received_item_unit_price) > 0
                                  ? money(item.received_item_unit_price)
                                  : "—"}
                              </TableCell>

                              <TableCell className="text-right">
                                {num(item.landed_price) > 0
                                  ? money(item.landed_price)
                                  : "—"}
                              </TableCell>

                              <TableCell className="text-right font-semibold">
                                {num(item.received_item_unit_total) > 0
                                  ? money(item.received_item_unit_total)
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex justify-end mt-4">
                    <div className="w-full md:w-80 space-y-1 p-4 bg-muted/30 rounded-lg border text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Items subtotal
                        </span>
                        <span>{money(selectedPO.item_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          GST ({num(selectedPO.gst_per)}%)
                        </span>
                        <span>{money(selectedPO.gst_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Labour</span>
                        <span>{money(selectedPO.extra_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transport</span>
                        <span>{money(selectedPO.transport_amount)}</span>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t font-semibold text-base">
                        <span>Grand Total</span>
                        <span className="text-primary">
                          {money(selectedPO.grand_amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowViewDialog(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {showLoader && <Loader />}
    </MainLayout>
  );
}